import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@components/atoms/Typography';
import { useThemes } from '@hooks/useThemes';
import { ResultsType } from '@types';
import styles from './Pagination.module.css';
import { useData } from '@hooks/useData';
import { getDynamicMenuItemValue } from '@utils/translation';

export interface PaginationProps {
    loading: boolean;
    type: ResultsType;
}

export const Pagination: FC<PaginationProps> = ({ loading, type }) => {
    const { t, i18n } = useTranslation();
    const { allSettings } = useData();

    const [translations, setTranslations] = useState({
        endOfProducts: '',
        endOfWorkers: '',
        endOfMerchants: '',
        endOfServices: '',
        endOfPointsServices: '',
        endOfOrders: '',
        endOfReservations: '',
        endOfPromotions: '',
    });

    const [endOfText, setEndOfText] = useState('');

    useEffect(() => {
        if (!allSettings?.data) return;

        // Compute all translations when data or language changes
        const newTranslations = {
            endOfProducts: t('pagination-component.end-of') + getDynamicMenuItemValue(allSettings.data, 1),
            endOfWorkers: t('pagination-component.end-of-workers'),
            endOfMerchants: t('pagination-component.end-of') + getDynamicMenuItemValue(allSettings.data, 3),
            endOfServices: t('pagination-component.end-of') + getDynamicMenuItemValue(allSettings.data, 2),
            endOfPointsServices: t('pagination-component.end-of-points-services'),
            endOfOrders: t('pagination-component.end-of-orders'),
            endOfReservations: t('pagination-component.end-of-reservations'),
            endOfPromotions: t('pagination-component.end-of-promotions'),
        };

        setTranslations(newTranslations);

        switch (type) {
            case ResultsType.products:
                setEndOfText(newTranslations.endOfProducts);
                break;
            case ResultsType.workers:
                setEndOfText(newTranslations.endOfWorkers);
                break;
            case ResultsType.merchants:
                setEndOfText(newTranslations.endOfMerchants);
                break;
            case ResultsType.services:
                setEndOfText(newTranslations.endOfServices);
                break;
            case ResultsType.pointsServices:
                setEndOfText(newTranslations.endOfPointsServices);
                break;
            case ResultsType.orders:
                setEndOfText(newTranslations.endOfOrders);
                break;
            case ResultsType.reservations:
                setEndOfText(newTranslations.endOfReservations);
                break;
            case ResultsType.promotions:
                setEndOfText(newTranslations.endOfPromotions);
                break;
            default:
                setEndOfText('');
                break;
        }
    }, [type, t, i18n.language, allSettings?.data]);

    return (
        <div className="d-flex flex-column align-items-center pt-4 gap-5" data-testid="pagination">
            {loading ? (
                <Loader />
            ) : (
                <Typography classes="mb-0" color="muted">
                    <>
                        {endOfText}
                    </>
                </Typography>
            )}
        </div>
    );
};

export const Loader: FC = () => {
    const { useGrayscaleTheme } = useThemes();

    return (
        <div
            className={
                // theme
                `${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
                // other classes
                ` ${styles.loader}`
            }></div>
    );
};
